import type { ReactNode } from 'react';
import React, { useCallback } from 'react';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ShortcutLozenge } from './ShortcutLozenge.tsx';

interface BOEQuickActionButtonProps {
	children: ReactNode;
	shortcut?: string;
	icon?: () => ReactNode;
	onClick?: () => void;
	onMouseEnter?: () => void;
	isDisabled?: boolean;
	isActive?: boolean;
	testId?: string;
	/** Button type for analytics tracking */
	analyticsButtonType?: 'cwr' | 'template' | 'pageElements' | 'import';
	/** Whether the current page is a live doc */
	isLivePage?: boolean;
}

const styles = cssMap({
	button: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
		'&:hover': {
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
	active: {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
	},
});

export const BOEQuickActionButton = ({
	children,
	shortcut,
	icon,
	onClick,
	onMouseEnter,
	isDisabled,
	isActive,
	testId,
	analyticsButtonType,
	isLivePage,
}: BOEQuickActionButtonProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		if (analyticsButtonType) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'BlankObjectExperienceButtons',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createWithRovoBlankObject',
					attributes: {
						variant: 'variant1',
						contentType: isLivePage ? 'liveDoc' : 'page',
						buttonType: analyticsButtonType,
					},
				},
			}).fire();
		}
		onClick?.();
	}, [onClick, createAnalyticsEvent, analyticsButtonType, isLivePage]);

	return (
		<Pressable
			testId={testId}
			onClick={handleClick}
			onMouseEnter={onMouseEnter}
			isDisabled={isDisabled}
			xcss={cx(styles.button, isActive && styles.active)}
			style={{
				border: `${token('border.width')} solid ${token('color.border')}`,
			}}
		>
			{icon?.()}
			{children}
			{shortcut && <ShortcutLozenge>{shortcut}</ShortcutLozenge>}
		</Pressable>
	);
};
